// Single source of truth for the build label shown in the UI.
// Bumped manually per release ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â kept here (not in package.json `version`)
// because the trailing letter denotes the channel (A = alpha) and isn't
// a valid SemVer suffix. Imported by the rail footer chip + tooltips so
// the user can tell at a glance which build is on production / staging
// / their dev tree.
//
// FORMAT: <MAJOR>.<MINOR>.<PATCH><CHANNEL>
//   MAJOR ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â bump on architectural milestones (1.x.x = multi-repo era)
//   MINOR ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â feature batches within a major
//   PATCH ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â incremental within a feature batch
//   CHANNEL ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â A=alpha, B=beta, etc. Do NOT roll the letter forward
//             as a sub-version.
//
// HISTORY:
//   0.1.x A  ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â legacy monorepo era (pre multi-repo split, retired May 2026)
//   1.0.1 A  ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â first multi-repo deploy: 9-repo split + all 7 BOS-
//              retirement TODOs closed + native /query RAG + S3 presign
//              + RRULE split + pnpm/tsx cutover. Continue from 1.0.2A
//              on the next ship.
//   1.1.x A  — multi-repo era feature batches (mail/calendar/tracker/CRM
//              + Emotional Pulse + AI chat + voice). Last: 1.1.967A.
//   1.2.0 A  — opens the next MINOR series (2026-06-26). Continue from
//              1.2.1A on the next ship.
//   1.2.1 A  — service: directed interaction edges for call/dm/channel
//              (Task 3.5b) — lights up stress/criticism-praise blocks.
//   1.2.3 A  — Today dashboard 3-column redesign (hybrid agenda +
//              multi-source efficiency + department workload).
//   1.2.4 A  — CRM Client-360 page: unified timeline (mail/meetings/
//              calls) + contacts + projects (auto+manual) + follow-ups,
//              backed by GET /api/crm/client-360/:id.
//   1.2.5 A  — Tracker screenshots: multi-employee + datetime filters,
//              keyset "Load more" pagination, in-page advanced lightbox.
//   1.2.7 A  — Tracker: internal default project (100% time attribution)
//              + cut dead Hubstaff sections (To-dos/Clients/Calendar/Teams).
//   1.2.10A  — voice: busy-room PUBLISHER cap for single-layer canvas
//              (background) cameras — they can't simulcast, so the
//              subscribe-side 180p cap couldn't step them down; now the
//              publisher shrinks its own sender to 180p/250k on 7+ camera
//              calls (RTCRtpSender.setParameters, no republish).
//   1.2.11 A — voice: ghost-killer cron reconciles VoiceState against the
//              LiveKit SFU (not just socket presence) + fans phantom
//              evictions to the server room so the sidebar self-corrects
//              (OSPROD-227, follow-up to OSPROD-225).
//   1.2.12 A — Tracker: time + cost (pay rate × hours) attributed to CRM clients vs partners.
//   1.2.14A  — voice: privacy background model load is now time-capped (15s).
//              A stalled ~15MB RVM fetch on a weak link used to show a
//              background-only (white) tile for the whole call; on timeout we
//              request the stock fallback so the person appears in bounded time.
//   1.2.16 A — Tracker: proactive alert push (notify) to server admins for critical/warning alerts.
//   1.2.17 A — voice: fix transcription queue head-of-line dam — failed
//              transcribe-session jobs no longer retain their jobId
//              (removeOnFail) and a permanently-bad audio file is skipped
//              instead of failing the whole session. Stuck-at-"processing"
//              transcripts (prod incident: 4k+ since 2026-06-12) now drain.
//   1.2.18 A — service: soft-deleted employees dropped from the Most-criticized /
//              praised + Stress-connections blocks (were surfacing as a raw UUID
//              row because emotionInteraction edges still carried the deleted id).
//   1.2.21 A — mail: toggleable Gmail-style calendar side panel (mini-month +
//              selected-day agenda) in the mail view (@os/calendar 1.13.36).
//   1.2.23 A — mail calendar panel reworked into a real calendar slice:
//              hydrates sources (events now show), + Create (QuickCreate)
//              and click-to-open EventPopover view/edit (@os/calendar 1.13.38).
//   1.2.24 A — client-access #0: durable CrmClient (access anchor surviving
//              cluster rebuilds) + manual create + "Give access" promote in CRM
//              curation; routes /api/crm/client-access (@os/crm 0.1.27).
//   1.2.26 A — voice mic-stuck recovery (2026-06-29 25-person standup
//              "mic didn't turn on → refresh"): (1) telemetry — record the
//              flag-false audio-desync branch inputs + room ConnectionState
//              into voice_diag rawStats, and fix the never-firing audio-gt-diag
//              append (out-of-scope `c`); (2) in-call "mic may not be
//              transmitting → Reconnect" banner (re-publish in place / rejoin,
//              no page refresh); (3) DEFAULT-OFF stranded-mic auto re-arm
//              (kill-switch voiceAutoMicReconnect). Invariant #72.
//   1.2.34 A — voice: flip the #72 stranded-mic auto re-arm to DEFAULT-ON
//              (validated live on prod 1.2.26A — recovered stranded mics, no
//              storm). Kill-switch is now opt-OUT: voiceAutoMicReconnect='off'.
//   1.2.35 A — service: register-aware emotion sentiment — profanity
//              outside English is treated as a normal conversational
//              register, not hostility (judge meaning/intent, not words);
//              directed hostility + English profanity keep their weight.
//              v4 scoring prompts (chat/call/mail.emotion) + in-place
//              backfill re-score of recent signals. Formula untouched.
//   1.2.46 A — voice: live polls (open/closed, anon/non-anon, real-time
//              tally) + synchronized PDF presentations in audio channels.
//              Host launches from the control bar; audience votes / follows
//              slides in sync. Authoring + run history in channel settings.
//              Server-authoritative via socket voice:${channelId} room
//              (@os/spaces 2.0.92).
//   1.2.48 A — auth (OSPROD-240): access/refresh token pair. Access token
//              now short-lived (1h); an expired access token is silently
//              renewed via POST /auth/refresh (single-flight axios
//              interceptor replays the original request) instead of
//              dropping the user to /login. Refresh token = JWT typ:'refresh'
//              (30d) bound to the revocable UserSession (logout/revoke kill
//              it). Session TTL 7d→30d. No schema change.
//   1.2.49 A — voice: edit an existing poll set in channel settings
//              (Edit button → prefilled editor → PUT /polls/:setId replaces
//              questions/options). Previously create + delete only.
//              (@os/spaces 2.0.93).
//   1.2.50 A — server-admin (OSPROD-247): "Clone from server" in Server
//              Admin → Roles (global-admin only) copies a server's whole
//              role structure (name/color/position/permissions, except
//              @everyone) into another — seeds a fresh server (PharmaStaff)
//              from a configured one (Rx2Go). Idempotent (skips same-name
//              roles). POST /servers/:id/roles/clone-from/:src (@os/spaces 2.0.94).
//   1.2.52 A — voice polls reworked to event-app realtime UX: answer ALL
//              questions first, THEN a full-area (over the tiles/screen-share)
//              live results page — bar charts for CLOSED, tag cloud for OPEN,
//              updating in real time. No per-question reveal. Only the host
//              closes it ("Hide poll"). Refresh-resilient via server
//              myAnswered (@os/spaces 2.0.95).
//   1.2.53 A — voice polls: view PAST run results from channel-settings
//              History — each run is clickable → modal with bar charts /
//              tag cloud (GET poll-runs/:runId/results reconstructs the run's
//              question set incl. soft-deleted-by-edit questions). (@os/spaces 2.0.97).
//   1.2.54 A — tasks (OSPROD-235): Board Settings' overlapping Members +
//              Sharing tabs merged into ONE "Access" tab — visibility,
//              open-to-all, per-user + department grants (member/admin),
//              all with Select2-like searchable pickers (by name/email)
//              instead of raw ID inputs. Old /members + /sharing paths
//              redirect to /access. (@os/taskboards 1.0.81).
//   1.2.55 A — tracker (OSPROD-236): admin-only screenshot delete. Trash
//              button on each shot → inline confirm → optimistic removal.
//              SOFT delete (CH deleted_at, read path filters it out; org-
//              scoped). Whole Tracker section is already admin-only.
//              (@os/tracker 1.1.57).
//   1.2.56 A — voice (OSPROD-109): camera enhancement ("Touch up my
//              appearance") — one slider (0–100) driving a WebGL stage
//              (skin smoothing + low-light lift + contrast/saturation pop)
//              as the 3rd stage of CombinedProcessor (zoom → background →
//              enhance). Device-local (localStorage cameraEnhance); passes
//              through unchanged on any WebGL failure. UI slider in the
//              video-settings row.
//   1.2.57 A — fix (OSPROD-237): Dashboard (and other admin-gated modules)
//              stopped flickering in/out of the rail. useVisibleModules no
//              longer resets to null (fail-open) on a transient refetch
//              failure — it keeps the last-known set — and a monotonic
//              request id drops out-of-order socket-triggered refetches so a
//              stale response can't overwrite a fresh module set.
//   1.2.58 A — tracker: 90-day screenshot retention. ClickHouse TTL
//              (taken_at + 90 DAY DELETE) on both screenshot tables +
//              S3 lifecycle raised 30d→90d (expire-tracker-screenshots-90d,
//              prefix tracker/) so image bytes + metadata expire together.
//              Applied to prod directly; durable via @os/tracker 1.1.58
//              (migration 007 + bucket-setup.ts).
//   1.2.59 A — voice (OSPROD-225 re-fix): sidebar voice-roster phantom that
//              survived 225 + 227. Root cause (confirmed on prod): an orphaned
//              presence:voice:<channel> Redis entry (userId:socketId left by an
//              ungraceful disconnect / deploy) shielded a stale VoiceState row
//              from the ghost-killer — its socket-presence fallback trusted the
//              orphan forever (a 12h-old phantom row observed). Fix: ghost-killer
//              uses listRooms to detect an authoritatively-CLOSED room and evicts
//              past-grace rows regardless of socket presence; the disconnect
//              handler now fans the leave delta to server:<id> so the sidebar
//              clears live. Server-only, no module bump.
//   1.2.60 A — security (tracker): global admins are NEVER tracked. The
//              effective-policy resolver short-circuits to fully-off for any
//              user.isAdmin, overriding all workspace/project/member policy +
//              server membership — the agent won't capture and ingest drops
//              any upload. Closes a privileged-screen data-exposure hole.
//              (@os/tracker 1.1.59).
//   1.2.62 A — voice (OSPROD-109 v2): the single master "Enhance" slider the
//              team disliked is split into THREE Zoom-style controls — "Touch
//              up my appearance" (skin smoothing), "Adjust for low light"
//              (gamma lift), "Vibrance" (contrast+saturation). Each drives its
//              own uniform in the WebGL EnhanceProcessor. localStorage
//              cameraEnhance is now JSON {smooth,lowlight,vibrance}; v1 numeric
//              value migrates → smooth. Server-only? No — os-core apps/web only.
//   1.2.64 A — seed: stage-only persistent global-admin test account
//              (aleksei@rx2go.ai). Gated on OS_FRONTEND_URL=stage so the
//              known weak credential never lands on prod; deploy:stage re-runs
//              the seed on every backend boot → account is always re-created
//              after a stage DB wipe.
//   1.2.65 A — voice: admin Mute + Force-mute of any participant. Hosts
//              (global/server/channel admin + channel owner) get a courtesy
//              Mute (target can unmute) and a locking Force mute (target
//              can't self-unmute in the channel; error dialog on attempt;
//              survives leave/rejoin via VoiceForceMute + VoiceState.forceMuted
//              mirror). Locked badge on tile + roster. Soft/client-cooperative.
//              (@os/spaces bump for the roster glyph.)
//   1.2.66 A — seed: add second stage-only global-admin test account
//              (gontcharovross@gmail.com); stage admin block refactored to a
//              list. Same stage gate — never seeded on prod.
//   1.2.70 A — myday: sign adapter selected nonexistent SignRecipient.createdAt
//              (prod error on every My Day build) → invitedAt + submission.createdAt.
//   1.2.71 A — notes: spreadsheet notes phase 1 (@os/notes 1.5.1) — Univer
//              SHEET note kind, sheet endpoints, lazy SheetEditor chunk,
//              json body-parser carve-out; web-builder heap bump.
//   1.2.74 A — notes: docs realtime phase 2 (@os/notes 1.6.1) — Yjs collab
//              over /notes-collab namespace, live cursors + presence,
//              update log + compaction cron; encrypted notes excluded.
//   1.2.79 A — notes: sheets realtime phase 3 (@os/notes 1.8.1) — Univer
//              mutation relay with Redis-ordered seq, live apply +
//              presence roster, snapshot resync on gap/reconnect.
//   1.2.80 A — notes: external link sharing phase 4 (@os/notes 1.9.1) —
//              NoteLinkShare tokens, guest REST + socket, public
//              /shared/:token page, ShareDialog link section.
//   1.2.81 A — notes: folder sharing (@os/notes 1.10.1) — FolderShare with
//              Google-Drive semantics (subtree cascade, container-owner
//              override, editor-creates-inside), kind-aware folder create
//              menu, shared-folder tree in the sidebar.
//   1.2.83 A — notes: unified sharing part A (@os/notes 1.11.1) — General
//              access (visibility) surfaced in one Share dialog for notes +
//              folders; folder visibility = subtree view grant (view-only).
//   1.2.86 A — notes: unified sharing part B (@os/notes 1.12.1) — external
//              tier: email invites (employee→internal, external→emailed
//              token link), folder external links, guest folder browser.
//   1.2.87 A — Home dashboard: new default Dashboards tab (/dashboards/home)
//              + "/" root landing. Aggregate GET /api/dashboard/home (10
//              adapters: reaching-you, mentions content-scan, 14d inbound
//              chart, emotion self-digest, projects pulse, sign/mail KPIs,
//              team leave/birthdays, voice presence, next up, my time;
//              TTL 3 min, ?refresh=1, X-Server-Id scopes admin Team).
//   1.2.91 A — voice (prod incident 2026-07-07, "фон сломался"): RVM background
//              died on prod because fc8964e1 pointed ORT's wasmPaths.mjs at
//              /ort/ort-wasm-simd-threaded.js, which docker/nginx.conf's
//              location /ort/ types{} block (no `js` mapping — types REPLACE,
//              not merge) served as application/octet-stream → strict
//              module-MIME rejected the import → session create failed for
//              every user → silent stock (MediaPipe) fallback with worse
//              edges, and the in-place fallback swap streamed the RAW room
//              for seconds on every camera-on (privacy!). Fix: (1) no mjs
//              key — the bundled ort embeds the glue; binary switched to
//              .jsep.wasm (pairs with the embedded JSEP glue); (2) nginx
//              /ort types maps js too; (3) privacy-ON stock fallback swaps
//              via unpublish → privacy republish (processor attached before
//              publish — raw never on the wire); invariants #75/#68.
//   1.2.88 A — mentions: DMMention/ChannelMention rows persisted on every
//              send (persistMentions at the socket DM/channel paths + REST
//              channel service; REST DM path via @os/messenger 2.0.63) —
//              the tables were write-orphaned. Home mentions + inbound
//              adapters switch from the interim content-scan to indexed
//              mention-table reads; idempotent 14d backfill script
//              (scripts/backfill-mentions.ts). My Day dms adapter links
//              gain the dm- prefix (bare /channels/<id> was a dead click
//              from the dashboards section).
//   1.2.97 A — voice: sticky ownership (single active device). A live voice
//              connection stays on ONE device; automatic paths (page-load
//              restore, socket-reconnect rejoin) no longer auto-take-over —
//              a server pre-check keyed on the JWT sid runs BEFORE room.connect()
//              (LiveKit identity == userId, so a non-owner connect evicts the
//              owner at the SFU). A second device shows a view-only banner +
//              "Move here" (dialog.confirm → takeover). Kill-switch
//              voiceStickyOwnership / VOICE_STICKY_OWNERSHIP. Invariant #77.
//   1.2.100A — files (OSPROD-289): Files → Departments now mirrors the FULL
//              org chart. The /tree lazy-provisioner used to create a dept's
//              root folder only when one of ITS members opened Files, so
//              freshly-created / memberless departments were missing and the
//              structure drifted from the org. It now provisions a dept_root
//              (+ dept_shared) for EVERY department in the server that lacks
//              one (idempotent, self-limiting; own-dept still gets member
//              subfolders). Refactor: ensureDepartmentRoots split out of
//              ensureServerRoots (dept-only, no company/personal side effects)
//              + one-time backfill script backfill-department-file-roots.ts.
//   1.2.101A — files (OSPROD-290/291/292): Files section UX. (290) Brow
//              breadcrumb shows the FULL path (Files › Dept › Shared › …)
//              with real folder names — every ancestor clickable, current
//              non-clickable; /folders/:id now returns the ancestor `path`.
//              (291) List / Grid view toggle with large lazy-loaded image
//              previews (icon fallback per file type). (292) Sort control
//              (name A–Z/Z–A, date, size, type) applied within folder- and
//              file-groups; view mode + sort persisted in localStorage.
//   1.2.102A — server-admin: Communication Governance "Policies" tab
//              (Task 8 of the comms-governance feature; backend from Tasks
//              1-7 was already live). New Server Admin tab — server-layer
//              enabled/enforcement-mode controls, three rule sections
//              (Forbidden topics / Trigger words / Barriers) each with
//              inline create+edit, per-rule enable toggle + delete, and a
//              paginated activity feed of rule matches. New components
//              under components/governance/{PoliciesTab,RuleForm,
//              ActivityTable,types}.tsx — RuleForm + ActivityTable are
//              self-contained/prop-driven so Task 9's department-layer
//              page can reuse them. Backend fix: rule PATCH silently
//              dropped `enabled` (missing from both the zod schema and the
//              governanceDb.governanceRule.update delegate type) — the
//              row-level enable toggle would have been a no-op; now wired
//              through routes/governance.ts + lib/governance/db.ts.
// 1.2.105A: ViewSX signup Plan 2 Task 8 — provisioning saga (BullMQ
//           `signup-provision` queue consumer, idempotent retry-safe steps
//           2-10 per spec §9) + signup-intent-expire / billing-grace-sweep
//           cron jobs + seat/module entitlement enforcement wiring
//           (finalizeRegistration + admin invite creation under
//           withSeatLock/assertBaseSeatAvailable; entitledModuleGate wraps
//           @os/spaces' requireEntitledModuleAccess for mail/calendar/crm/
//           tracker/notes/sign + desktop/monitoring/assistant) + admin
//           signup ops endpoints (GET list, retry-provisioning, cancel).
// 1.2.106A: ViewSX signup Plan 2 Task 10 - fix(stripe): handleInvoicePaymentFailed
//           now returns 'ignored' (not retryable 'error:DEFERRED') when the
//           invoice is structurally unlinked (no subscription id at all),
//           mirroring invoice.paid's earlier F18 fix, + a mirroring unit
//           test. Plus docs: CODEBASE_INDEX.md new section 3.39 (Public
//           Signup API routes/lib/jobs map) + 3.30b/4.2/6/7 additions, and
//           the new operations runbook docs/superpowers/runbooks/2026-07-18-
//           viewsx-signup-operations.md.
// 1.2.110A: SubscriptionCard PRODUCT_LABEL: friendly names for the 4 Wave 2
//           self-service extensions (knowledge_base, learning_lms, vault,
//           service_desk) so purchased seats don't render as raw slugs.
//           (1.2.109A reserved by the wave2-internal-billing branch.)
// 1.2.111A: ViewSX Wave 2 — 4 paid extensions (KB/LMS/Vault/Service Desk,
//           catalog 2026-07-19.1, dashboards free in base) + internal
//           (dogfood) subscriptions: ledger-billed workspaces at live
//           catalog prices; legacy telephony/desktop module charges
//           superseded for covered servers.
// 1.2.113A: signup: removed the SIGNUP_ENABLED kill-switch entirely — the
//           public API is unconditionally mounted now (DC decision
//           2026-07-19: "no point in the flag — we open as soon as we
//           finish"). POST /:id/checkout and the seat-change endpoint
//           gained an isStripeConfigured() capability guard (503
//           CHECKOUT_NOT_CONFIGURED) as the only remaining gate.
// 1.2.114A: billing: desktop pricing split (DC-approved 2026-07-19) —
//           catalog 2026-07-19.2 replaces desktop_workspace ($49 flat) with
//           desktop_linux ($29/user/mo) + desktop_windows ($49/user/mo),
//           both MANAGED. Internal (dogfood) subscriptions now bill desktop
//           by ACTUAL per-OS usage (countDesktopTerminals), not seats.
//           desktop_workspace stays a deprecated legacy alias so the
//           'desktop' module gate keeps working without an @os/spaces
//           republish (enrollInternalSubscription writes the alias
//           ServerEntitlement + enabledModules convergence, never
//           itemsJson). Fixed a supersede-guard bug along the way:
//           desktopMetering's chargeDesktopMonth now checks
//           internalSubCoversAnyOf(['desktop_workspace','desktop_linux',
//           'desktop_windows']) instead of the single legacy key, so
//           re-enrolling an existing internal subscriber into the split
//           can't silently resume double-billing.
// 1.2.115A: billing: SubscriptionCard PRODUCT_LABEL gains the managed
//           product names (Business Phone / Advanced AI Operations /
//           Desktop Workspace — Linux / — Windows / Support + Probe, plus
//           the pre-split desktop_workspace legacy alias) so managed line
//           items in a subscription's itemsJson render friendly names
//           instead of raw slugs.
// 1.2.119A: ViewSX Trust signed Desktop posture — Ed25519 device identity,
//            protected key storage, bounded tamper-evident offline queue,
//            authenticated ack-before-delete, and narrow Electron IPC bridge.
// 1.2.120A: ViewSX Trust production Desktop provisioning + native posture
//            collection with closed acknowledgement validation.
// 1.2.121A: ViewSX Trust native posture now reports affirmative facts only
//            from complete OS policy evidence (all Windows firewall profiles,
//            password-backed screen locking, scheduled automatic updates,
//            and the Linux root filesystem's dm-crypt/LUKS ancestry).
// 1.2.122A: ViewSX Trust automatic-update posture requires full macOS
//            download/install/security policy or complete Linux systemd +
//            APT periodic + allowed-origin policy; weak signals remain unknown.
// 1.2.124A: ViewSX Trust production hardening: strict tenant entitlement,
//            deployable DB lifecycle, content-addressed exports, durable
//            remediation retries, and runtime host/render verification.
// 1.2.125A: ViewSX Trust host verification round 2: fail-closed primary rail,
//            worker scheduler controls, real app/shell integration coverage,
//            and lazy Trust route loading.
// 1.2.126A: ViewSX Trust production host composition, exact-active access,
//            machine credentials, and durable Probe telemetry runtime.
// 1.2.127A: Durable Probe pairing/offboarding/rotation, signed stale replay,
//            native Windows/macOS posture, and canonical root-pnpm executable packaging.
// 1.2.128A: ViewSX Trust release hardening: protected deployment overlay,
//            real native/AWS ports, external connector setup/OAuth/webhooks,
//            authenticated rotation confirmation, and fail-closed Probe
//            offboarding/credential renewal.
// 1.2.129A: Run release-secret rendering and validation in disposable Node
//            containers because the protected shell deploy runner has no Node runtime.
// 1.2.130A: Trust promote-gate repairs: lazy trust module manifest (restores
//            vi.mock isolation), container-aware contract tests, awaited
//            bootstrap in the full chain, deploy fill-loop covers the blanked
//            template secrets and fails closed on empty critical keys,
//            log_bin_trust_function_creators for trigger migrations.
// 1.2.135A: HR Phase-2 candidate lifecycle — os-core consumer wiring:
//            hrLifecycle seams (guest voice invites via spaces channel-invite
//            rows, TranscriptSession finder, @os/sign adapter + sign-event
//            subscriber → @os/hr handleSignEvent) wired in server AND worker;
//            HR rail badge (upcoming interviews, poll-on-nav); public routes
//            /interview/:token, /interview/cancel/:token,
//            /candidate-details/:token; lifecycle notification templates
//            migration; HR_PII_KEY / HR_SIGN_OWNER_ID env plumbing.
// 1.2.137A: infra: LiveKit TURN/TLS moves off public 0.0.0.0:443 to
//            127.0.0.1:5349. The app box has one public IP and :443 now
//            serves two tenants — TURN and the ViewSX landing (viewsx.ai).
//            Host nginx owns public :443 and splits by SNI via ssl_preread
//            WITHOUT terminating TLS, so LiveKit still presents its own
//            turn.os.rx2go.ai cert and `turns:turn.os.rx2go.ai:443` is
//            unchanged for clients. Compose-only change; no app code.
// 1.2.138A: fix(signup): the public signup API's own permissive CORS never
//            applied to preflights. The app-wide allowlist cors() in app.ts
//            was registered first and answers OPTIONS itself, so a preflight
//            from the landing origin got 204 with no Access-Control-Allow-
//            Origin and the browser blocked it — killing the entire
//            self-service signup flow from viewsx.ai while simple GETs (not
//            preflighted) still looked fine. Mounts cors({origin:'*'}) for
//            /api/public/signup BEFORE the app-wide one. Found the day the
//            landing first went live.
// 1.2.139A: infra(log hygiene): explicit json-file log caps on the two
//            long-lived containers that were growing unbounded — frontend
//            (nginx access log, 20 GB) and livekit-sip (2 GB). Neither is
//            recreated by a normal deploy, so they never inherited the
//            x-logging anchor added in the 2026-06-13 audit. Compose-only;
//            takes effect on the next --force-recreate of those services.
//            Companion prod-side actions (applied directly, not in this
//            MR): 30-day TTL on the eight ClickHouse system.*_log tables
//            (they had none — 43 GB) and a 3-day column TTL on
//            voice_diag_samples.raw_stats (95% of that table's 6.9 GB;
//            aggregate telemetry columns keep the full 14 days).
// 1.2.140A: docs fix on top of 139A. The claim that the new frontend /
//            livekit-sip log caps need a separate off-hours recreate was
//            wrong: adding `logging:` changes the service spec, so the
//            deploy's own `up -d --no-deps frontend` (step 5b, normally a
//            no-op) recreates the container once and the cap goes live
//            with the deploy. Comments + runbook corrected; no behaviour
//            change.
// 1.2.145A: billing: White Label added to the catalog (2026-07-27.1) as two
//            org_flat MANAGED products — white_label_web $500/mo + $2,500,
//            white_label_apps $1,500/mo + $12,000 (apps requires web).
//            Priced per organisation because the cost of running a tenant
//            under its own brand does not move with seat count; the apps
//            tier is priced on RECURRING cost, since every release must be
//            rebuilt, re-signed and re-submitted per brand. Managed here
//            means provisioning needs a human (domain, mail domain, signing
//            identity, store accounts) — not that the price is hidden.
//            Rationale: docs/superpowers/specs/2026-07-27-viewsx-white-label-design.md
// 1.2.146A: fix(history): session-participant capture is now idempotent under
//            concurrency. `sessionParticipant.upsert` races itself — when the
//            same person emits voice:join twice at once (two tabs, or a
//            reconnect re-join overlapping the original) both calls SELECT-miss
//            and both INSERT, and the loser threw P2002 on
//            SessionParticipant_sessionId_userId_key. It was caught and logged
//            as "[history] participant capture failed", so calls were fine but
//            the history roster silently lost the row. Join churn on prod is
//            high (16368 joins vs 7035 leaves over 21 days), so this fired
//            regularly. All three capture sites (authed voice:join, guest
//            join, phone leg) now go through captureParticipant(), which folds
//            P2002 into an update — the row exists, which is the desired end
//            state. voice:join semantics unchanged.
// 1.2.141A: ViewSX Device Management workspace: explicit opt-in module rail,
//            tenant-scoped authenticated transport, and nine lazy pages.
// 1.2.142A: ViewSX MDM destructive approval reauthentication: current-password
//            verification mints a five-minute, one-shot Redis assertion bound
//            to tenant/user/login-session/action/command; only the exact
//            approve route can consume it into the server-trusted actor.
// 1.2.148A: rebased MDM foundation delivery on the current staging release.
// 1.2.149A: white label phase 1 — per-domain branding. Brand model
//            (soft-deleted, no FKs) + Host-header resolver with a 60s cache
//            + public GET /api/brand (no auth: the login page needs it before
//            a JWT exists, hence Vary: Host + no-cache). Web applies name,
//            favicon and accent at boot; rail / mobile bar / login / register
//            / reset render the brand instead of a hardcoded mark. A tenant
//            accent is contrast-checked and refused if unreadable, and the
//            accent is NEVER applied on the default brand — that would
//            repaint --color-primary on every unbranded deployment,
//            os.rx2go.ai included. Stock short name is now 'Business OS';
//            the rail used to hardcode a customer's name.
//            KNOWN GAPS, tracked: inline boot splash still stock, and brand
//            LOGOS unreachable while /api/blob is JWT-gated — surfaces
//            degrade to the brand name, never a broken image.
//            Spec: docs/superpowers/specs/2026-07-27-viewsx-white-label-design.md
// 1.2.151A: white label — public brand-asset route. GET /api/brand/asset/:slot
//            (logo-light | logo-dark | favicon), no auth, closing the Phase 1
//            gap where brand logos were unreachable because /api/blob is
//            JWT-gated. Deliberately keyed on a SLOT, never a file id: making
//            /api/blob public, or taking an id here, would turn a branding
//            feature into an unauthenticated read primitive over the whole
//            file store. A caller can only ever obtain the three assets of the
//            brand that owns the host they are already talking to, so there is
//            nothing to enumerate. Vary: Host (a shared cache must not serve
//            one tenant's logo to another) and a short max-age, not immutable,
//            since the URL is versionless and its content changes on brand edit.
//            Every failure path returns 404, never 500 — a half-configured
//            brand must not break a login page.
// 1.2.152A: OSPROD-275 — a member can belong to several departments.
//            ServerMember.departmentId stays the PRIMARY one (org chart,
//            approver routing, sidebar grouping all need a single answer);
//            the full set lives in a new nullable Json departmentIds, primary
//            first. Every department-scoped subsystem now honours the whole
//            set — channels, files, DAV, vacations, birthdays, emotion
//            ratings, governance — which does widen visibility of personal
//            data for anyone given a second team. Approver routing and the
//            displayed department label stay primary-only on purpose.
//            (@os/spaces 2.0.111.)
// 1.2.153A: prod fatal-exit observability — the handler logged only the
//            error NAME, so every real crash on prod read as a bare
//            `[server-fatal] Error` with no message and no stack. Backends
//            were exiting several times a day (nginx upstream failures show
//            the same pattern back to at least 2026-07-21) and there was
//            nothing to diagnose from. Now the message and stack are
//            reported; the Trust-config carve-out keeps its verbatim text.
// 1.2.154A: white label — brand admin write path. Global-admin-only CRUD at
//            /api/admin/brands plus per-slot asset upload, and a White label
//            tab in the platform admin. This is the half that was missing:
//            brand slots could previously only be filled by a direct DB write.
//            Soft delete does NOT just stamp deletedAt — it also rewrites both
//            unique columns to a `deleted:<id>:<original>` tombstone, because
//            MySQL plain uniques mean a dead row would otherwise keep holding
//            primaryDomain and block ever re-creating a brand for that domain.
//            The colon is illegal in a hostname AND is where normalizeHost()
//            truncates, so no Host header can resolve to a tombstone even if a
//            query loses its deletedAt filter. Assets reject SVG (served
//            inline from a public route = script container) and verify magic
//            bytes against the declared mime, since multipart Content-Type is
//            client-supplied. Every write invalidates the 60s resolver cache.
//            Spec: ai/os (umbrella) docs/superpowers/specs/2026-07-27-viewsx-white-label-design.md
// 1.2.156A: unblock the prod gate — the two test files that OSPROD-275
//            (multi-department) left behind still mocked the primary-only
//            serverMember.findFirst / getMemberDepartmentId, and the admin
//            registry test still expected 11 platform sections after White
//            label added `brands` at order 137. Nothing shipped was wrong;
//            the suite only runs on `production`, so main never caught it.
//            Also covers the new behaviour: heading a SECONDARY department
//            is enough to see a birthday / approve a vacation.
// 1.2.157A: fix(realtime): stop dropping every module event. bosEvents
//            validated only the Python BOS envelope, so the calendar and mail
//            module publishers — a different, flat {kind,userId,...} shape —
//            were rejected wholesale: ~470 dropped events/hour on prod, i.e.
//            realtime mail and calendar never worked from the modules. The
//            subscriber now accepts BOTH producers; module events route to
//            user:<uuid>, Python keeps agent:<n> byte-for-byte. Routing by
//            userId, not bosAgentId, because 181 of 385 prod users have a NULL
//            bosAgentId — resolving to it would have fixed realtime for
//            barely half of them, permanently. Module frames carry kind:'sync'
//            and NO per-item id: one event covers a whole sync pass, so
//            kind:'new' would make MailConnectingModal report "1 message" for
//            a 200-message backfill, and a fabricated id is something a
//            consumer could later key a cache off.
// 1.2.160A: AI Operator flagship acceptance — production evidence inference,
//            complete-provenance authorization, stable review packages, and
//            the closed CRM 0.1.31 workspace boundary.
// 1.2.161A: Windows MDM host composition boundary, protocol isolation,
//            startup validation, and disabled-by-default production runbook.
// 1.2.162A: Bound best-effort production seeding so a stalled Prisma teardown
//            cannot prevent the backend HTTP server from starting.
// 1.2.163A: Include the Vitest operator source-resolution plugin in the
//            production backend image so the registry-mode test gate loads.
// 1.2.164A: Operator acceptance in the production-image gate now uses an
//            explicit pipeline-bound MySQL/Redis contract instead of trying
//            to load the local .env.test file; invalid or mismatched CI
//            markers, hosts, database names, and NODE_ENV fail closed. MDM
//            host-contract tests read runbooks from the existing read-only
//            repository mount because the runtime image excludes docs.
// 1.2.165A: Operator real-database acceptance resolves its correlated
//            timeline event with a soft-delete-safe scoped lookup, restoring
//            all seven production-image module contract checks.
// 1.2.166A: Operator proposal execution, correction/revival/rejection, and
//            plan revision use active workspace-scoped reads compatible with
//            the production soft-delete Prisma extension.
// 1.2.167A: Concurrent Operator audit-outbox flushes use duplicate-safe bulk
//            delivery with exact read-back verification, eliminating
//            load-sensitive P2002s without acknowledging skipped writes.
// 1.2.168A: Managed Device Management pricing and entitlement contracts,
//            including base-subscription enforcement and platform-specific
//            desktop/mobile device quantities.
// 1.2.169A: MDM enrolled-device billing meter consumer: lazy host-owned,
//            tenant-scoped aggregate measurements with fail-closed validation.
// 1.2.170A: Keep the production MDM host contract aligned with the published
//            @os/mdm 0.2.1 consumer release intent.
// 1.2.171A: Provider-independent durable MDM billing state, aggregate
//            measurement audit, conservative decrease reducer, and ordered
//            quantity outbox. No provider activation or delivery job.
// 1.2.173A: Prod stability (2026-08-05 outage): session audio recorder
//            guards the write-after-end race and owns a non-fatal stream
//            'error' listener; unhandled promise rejections log
//            [server-nonfatal] instead of exiting the backend.
// 1.2.174A: Voice reconnect-storm fix (2026-08-06): central exponential
//            backoff on auto LiveKit connects (one client rejoined 171×/day
//            churning whole rooms), the OSPROD-288 rejoin ladder only
//            re-arms after 60s of stable room, and transcription audio
//            chunks use volatile emit so a half-dead socket can't buffer
//            gigabytes client-side.
// 1.2.175A: Desktop shell Electron 30.5.1 → 43.3.0 (Chromium 124 → 150,
//            Node 20 → 24; E30 was EOL since Oct 2024). Desktop 1.0.34.
//            Screen-share handler adapted to E43 semantics: deny via
//            callback(null) ({} now throws), one-shot callback guard,
//            loopback audio only when the page requested audio (an
//            unrequested audio stream now stalls getDisplayMedia).
export const APP_VERSION = '1.2.197A'

// Build-time snapshot of the @os/* module versions compiled into THIS
// SPA bundle, plus the build timestamp. Both are injected by
// vite.config.ts `define` (see readModuleVersions there). Surfaced in
// the rail version popover so the exact module set shipped in a build
// is visible at a glance ÃƒÂ¢Ã¢â€šÂ¬Ã¢â‚¬Â a stale frontend deploy stops being a
// guessing game. Falls back to empty/'' under vitest (no define).
export const MODULE_VERSIONS: Record<string, string> =
  typeof __MODULE_VERSIONS__ !== 'undefined' ? __MODULE_VERSIONS__ : {}

export const BUILD_TIME: string =
  typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : ''
